if (!customElements.get('coaching-waitlist-gate')) {
  customElements.define(
    'coaching-waitlist-gate',
    class CoachingWaitlistGate extends HTMLElement {
      connectedCallback() {
        this.allowClose = this.dataset.allowClose === 'true';

        this.overlay = this.querySelector('[data-gate-overlay]');
        this.modal = this.querySelector('[data-gate-modal]');
        this.closeButton = this.querySelector('[data-gate-close]');
        this.successState = this.querySelector('[data-gate-success]');
        this.bodyField = this.querySelector('[data-gate-body]');
        this.form = this.querySelector('form');

        this.previouslyFocused = document.activeElement;

        if (this.hidden) return;

        this.lock();

        // This is a permanent gate: it has no unlock state, before or after
        // a successful submission. If successState is present the form
        // already re-rendered as the success message (form.posted_successfully?
        // in the section), but the gate, backdrop, and lock stay exactly as
        // they are - only focus moves so screen readers land on the message.
        if (this.successState) {
          this.successState.setAttribute('tabindex', '-1');
          this.successState.focus({ preventScroll: true });
          return;
        }

        this.bindEvents();
        this.focusFirstField();
      }

      bindEvents() {
        this.closeButton?.addEventListener('click', () => {
          if (this.allowClose) this.dismissForSession();
        });

        this.addEventListener('keydown', (event) => {
          if (event.key === 'Escape' && this.allowClose) {
            this.dismissForSession();
            return;
          }
          if (event.key === 'Tab') this.trapFocus(event);
        });

        this.form?.addEventListener('submit', () => this.composeBody());
      }

      // offsetParent is null for display:none (and detached) elements, and
      // combined with a getClientRects() check also catches visibility:hidden.
      // Needed because third-party scripts (e.g. Shopify's hCaptcha check)
      // inject their own hidden fields into the form after page load, and
      // those don't necessarily carry a `hidden` attribute we could select
      // against.
      isVisible(el) {
        return el.offsetParent !== null && el.getClientRects().length > 0;
      }

      getFocusable() {
        const candidates = this.modal.querySelectorAll(
          'button:not([disabled]), input:not([disabled]):not([type="hidden"]), select:not([disabled]), textarea:not([disabled]), a[href]'
        );
        return Array.from(candidates).filter((el) => this.isVisible(el));
      }

      trapFocus(event) {
        const focusable = this.getFocusable();
        if (!focusable.length) return;
        const first = focusable[0];
        const last = focusable[focusable.length - 1];

        if (event.shiftKey && document.activeElement === first) {
          event.preventDefault();
          last.focus();
        } else if (!event.shiftKey && document.activeElement === last) {
          event.preventDefault();
          first.focus();
        } else if (!this.modal.contains(document.activeElement)) {
          event.preventDefault();
          first.focus();
        }
      }

      focusFirstField() {
        const target = this.getFocusable()[0];
        target?.focus({ preventScroll: true });
      }

      composeBody() {
        const value = (name) => this.querySelector(`[name="${CSS.escape(name)}"]`)?.value?.trim() || '';

        const lines = [
          '1-ON-1 COACHING WAITLIST',
          '----------------------------------------',
          `First name: ${value('contact[first_name]')}`,
          `Last name: ${value('contact[last_name]')}`,
          `Email: ${value('contact[email]')}`,
          `Phone: ${value('contact[phone]')}`,
          `Primary goal: ${value('goal')}`,
          `Current training level: ${value('level')}`,
          `Preferred start timeframe: ${value('timeframe')}`,
          `Message: ${value('message')}`,
          'Source: 1-on-1 Coaching Waitlist',
        ];

        if (this.bodyField) {
          this.bodyField.value = lines.join('\n');
        }
      }

      lock() {
        document.documentElement.classList.add('coaching-gate-locked');
      }

      unlockScroll() {
        document.documentElement.classList.remove('coaching-gate-locked');
      }

      dismissForSession() {
        // Closing without submitting hides the gate for this page view only;
        // it does not persist anywhere and reappears on the next load. This
        // is unrelated to (and not a path toward) the permanent unlock this
        // gate no longer has - it's just a same-view dismissal.
        this.hidden = true;
        this.unlockScroll();
        this.previouslyFocused?.focus?.();
      }
    }
  );
}
